import React, { useEffect, useRef, useState } from 'react'
import DetailsBox, {
  DetailRow,
} from '../../../../../components/Cards/DetailsBox'
import moment from 'moment'
import SmallModal from '../../../../../components/Modals/SmallModal'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import API from '../../../../../services/API'
import PDFViewer from '../../../../../components/Modals/PDFViewer'

//@MUI
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material'
import {
  Add,
  Article,
  AutoAwesome,
  CalendarMonth,
  CheckCircle,
  CloudUpload,
  Delete,
  Edit,
  ErrorOutline,
  GppGood,
  LocalPolice,
  ManageSearch,
  PendingActions,
  Shield,
  WarningAmber,
} from '@mui/icons-material'
import BigModal from '../../../../../components/Modals/BigModal'
import AnDateField from '../../../../../components/DateTime/DateField'
import ConfirmDelete from './DBS/ConfirmDelete'

const DBSCheck = ({ employeeData, loadEmployee }) => {
  const { employee } = employeeData
  const [openAddNew, setOpenAddNew] = useState(false)
  const [date, setDate] = useState(null)
  const [number, setNumber] = useState('')
  const [updateService, setUpdateService] = useState(false)
  const [attachment, setAttachment] = useState()
  const [openPDF, setOpenPDF] = useState(null)
  const [pendingUpload, setPendingUpload] = useState(false)
  const fileInput = useRef()
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false)
  // Delete requested from a card's own button (rather than from inside the
  // click-through viewer panel, which works off openPDF)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [fileError, setFileError] = useState('')
  const [openEdit, setOpenEdit] = useState(null)

  useEffect(() => {
    if (openEdit) {
      setDate(moment(openEdit.completed))
      setNumber(openEdit.reference)
      setUpdateService(openEdit.isOnUpdateService === false ? false : true)
    }
  }, [openEdit])

  const DBSInfoPanel = (props) => (
    <Stack gap={1}>
      <DetailRow
        label="Date"
        value={moment(openPDF?.completed).format('DD/MM/YYYY')}
      />
      <DetailRow label="Reference" value={openPDF?.reference} />
      {props.children}
      <Button
        variant="contained"
        size="small"
        color="anBlue"
        onClick={() => setOpenEdit(openPDF)}
      >
        Edit
      </Button>
      <Button
        variant="contained"
        size="small"
        color="error"
        onClick={() => setOpenConfirmDelete(true)}
      >
        Delete
      </Button>
      <SmallModal
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
      >
        <ConfirmDelete
          s3key={openPDF?.attachment}
          dbsID={openPDF?._id}
          onClose={() => setOpenConfirmDelete(false)}
          employeeID={employee._id}
          loadEmployee={loadEmployee}
        />
      </SmallModal>
    </Stack>
  )
  const handleAttachmentAdd = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Reset input so selecting the same file again triggers onChange
    e.target.value = ''

    const allowedMime = ['application/pdf']
    const allowedExt = ['pdf']

    const ext = file.name.split('.').pop()?.toLowerCase()

    // MIME is the main check; extension is a backup (some OS/browser combos provide empty/odd MIME)
    if (!allowedMime.includes(file.type) || !allowedExt.includes(ext)) {
      setFileError('Please upload a PDF file only.')
      setAttachment(undefined)
      return
    }

    setFileError('')
    setAttachment(file)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    setPendingUpload(true)

    let s3key
    let s3url

    const submit = () => {
      if (openEdit) {
        API.put(`/employees/dbscheck/${openEdit._id}`, {
          reference: number,
          completed: date,
          isOnUpdateService: updateService,
          deleteOldAttachment: !!attachment,
          attachment: attachment && s3key,
        }).then((res) => {
          if (res.status === 200) {
            window.location.reload()
          }
        })
      } else {
        API.post('/employees/dbsCheck', {
          employee: employee._id,
          reference: number,
          completed: date,
          // Always send the explicit boolean: a missing value reads as
          // "monitored" everywhere (isOnUpdateService !== false), which made
          // unchecked-on-create records come back labelled "Update Service"
          isOnUpdateService: updateService,
          attachment: attachment && s3key,
        })
          .then((res) => res.status === 200 && window.location.reload())
          .catch((e) => console.log(e))
      }
    }
    if (attachment) {
      // TODO: Should this be done on the backend instead of sharing URL to frontend?
      await API.post('gets3uploadUrl', {
        folder: 'employees/dbs',
        extension: attachment.type.split('/')[1],
      })
        .then((res) => {
          s3url = res.data.url
          s3key = res.data.key
          API.put(s3url, attachment, {
            headers: {
              'Content-Type': 'application/pdf',
            },
          }).then(() => submit())
        })
        .catch((e) => console.log(e))
    } else {
      submit()
    }
  }

  return (
    <Grid item xs={12} lg={6}>
      <DetailsBox
        title="DBS Checks"
        noBorder
        buttonRight={
          <Button
            size="small"
            variant="standard"
            color="anPurple"
            startIcon={<Add />}
            onClick={() => {
              setOpenAddNew(true)
              setDate(null)
              setNumber('')
              setUpdateService(false)
              setAttachment(undefined)
            }}
            sx={{
              borderRadius: 1,
            }}
          >
            Add DBS
          </Button>
        }
      >
        <Stack spacing={2}>
          {!employee?.dbsChecks?.length && (
            <Alert
              severity="info"
              icon={<PendingActions />}
              sx={{ borderRadius: 3 }}
            >
              No DBS checks have been added for this employee yet.
            </Alert>
          )}

          <Grid container spacing={1.5}>
            {employee?.dbsChecks?.map((check) => {
              const attemptStatus = check?.lastUpdateServiceCheck?.status
              // The last answer the DBS API actually gave about this
              // certificate. Older records predate
              // lastDefinitiveUpdateServiceCheck, so fall back to the latest
              // attempt when that attempt was itself definitive.
              const verdictStatus =
                check?.lastDefinitiveUpdateServiceCheck?.status ??
                ([
                  'BLANK_NO_NEW_INFO',
                  'NON_BLANK_NO_NEW_INFO',
                  'NEW_INFO',
                  'NO_MATCH_FOUND',
                ].includes(attemptStatus)
                  ? attemptStatus
                  : undefined)
              // Transient failure - the DBS API was unreachable or in
              // maintenance. Must not be presented as a certificate problem.
              const attemptFailed = [
                'CHECK_FAILED',
                'INVALID_REQUEST',
              ].includes(attemptStatus)

              const withStaleNote = (chip) =>
                attemptFailed ? (
                  <Tooltip
                    arrow
                    title="The latest automated check couldn't reach the DBS Update Service - showing the last successful result"
                  >
                    {chip}
                  </Tooltip>
                ) : (
                  chip
                )

              const statusChip = (() => {
                if (verdictStatus === 'BLANK_NO_NEW_INFO') {
                  return withStaleNote(
                    <Chip
                      size="small"
                      color="success"
                      icon={<CheckCircle />}
                      label="Current - blank"
                    />,
                  )
                }

                if (verdictStatus === 'NON_BLANK_NO_NEW_INFO') {
                  return withStaleNote(
                    <Chip
                      size="small"
                      color="info"
                      icon={<GppGood />}
                      label="Current - has info"
                    />,
                  )
                }

                if (verdictStatus === 'NEW_INFO') {
                  return withStaleNote(
                    <Chip
                      size="small"
                      color="error"
                      icon={<WarningAmber />}
                      label="New info"
                    />,
                  )
                }

                if (verdictStatus === 'NO_MATCH_FOUND') {
                  return withStaleNote(
                    <Chip
                      size="small"
                      color="warning"
                      icon={<ManageSearch />}
                      label="No match"
                    />,
                  )
                }

                if (attemptStatus === 'MISSING_REQUIRED_DATA') {
                  return (
                    <Tooltip
                      arrow
                      title="Update Service checks can't run - date of birth, surname, or the organisation declaration is missing"
                    >
                      <Chip
                        size="small"
                        color="warning"
                        icon={<ErrorOutline />}
                        label="Missing details"
                      />
                    </Tooltip>
                  )
                }

                if (attemptFailed) {
                  return (
                    <Tooltip
                      arrow
                      title="Couldn't reach the DBS Update Service - the check will retry automatically"
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<ErrorOutline />}
                        label="Check failed"
                      />
                    </Tooltip>
                  )
                }

                if (check.isOnUpdateService !== false) {
                  return (
                    <Chip
                      size="small"
                      color="primary"
                      icon={<AutoAwesome />}
                      label="Update Service"
                    />
                  )
                }

                return (
                  <Chip size="small" variant="outlined" label="Manual record" />
                )
              })()

              return (
                <Grid item xs={12} key={check._id}>
                  <Box
                    onClick={() => setOpenPDF(check)}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 8px 26px rgba(15,23,42,0.06)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 14px 34px rgba(15,23,42,0.12)',
                        borderColor: 'primary.light',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 5,
                        bgcolor:
                          verdictStatus === 'NEW_INFO'
                            ? 'error.main'
                            : verdictStatus === 'NO_MATCH_FOUND' ||
                                attemptStatus === 'MISSING_REQUIRED_DATA'
                              ? 'warning.main'
                              : attemptFailed && !verdictStatus
                                ? 'grey.400'
                                : check.isOnUpdateService !== false
                                  ? 'success.main'
                                  : 'grey.300',
                      },
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={2}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: 2.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: check.attachment
                                ? 'rgba(33,150,243,0.10)'
                                : 'rgba(158,158,158,0.12)',
                              color: check.attachment
                                ? 'info.main'
                                : 'text.secondary',
                            }}
                          >
                            <Article />
                          </Box>

                          <Box>
                            <Typography fontWeight={800}>
                              DBS Certificate
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Ref: {check.reference || 'No reference recorded'}
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                        >
                          {statusChip}
                          <Tooltip title="Edit this DBS record">
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ minWidth: 0, px: 1, borderRadius: 2 }}
                              onClick={(e) => {
                                // The card itself opens the viewer — keep the
                                // edit affordance visible and independent
                                e.stopPropagation()
                                setOpenEdit(check)
                              }}
                            >
                              <Edit fontSize="small" />
                            </Button>
                          </Tooltip>
                          <Tooltip title="Delete this DBS record">
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              sx={{ minWidth: 0, px: 1, borderRadius: 2 }}
                              onClick={(e) => {
                                e.stopPropagation()
                                setDeleteTarget(check)
                              }}
                            >
                              <Delete fontSize="small" />
                            </Button>
                          </Tooltip>
                        </Stack>
                      </Stack>

                      <Divider />

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                      >
                        <Stack
                          direction={{ xs: 'column', lg: 'row' }}
                          spacing={1}
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <CalendarMonth fontSize="small" color="action" />
                            <Typography variant="body2">
                              <strong>
                                {moment(check.completed).format('DD/MM/YYYY')}
                              </strong>
                            </Typography>
                          </Stack>
                        </Stack>

                        <Stack direction="row" spacing={1}>
                          {check.attachment && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label="PDF attached"
                            />
                          )}

                          {check.lastUpdateServiceCheck?.date && (
                            <Tooltip
                              title={`Last checked ${moment(
                                check.lastUpdateServiceCheck.date,
                              ).format('DD/MM/YYYY HH:mm')}`}
                            >
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`Last checked ${moment(check.lastUpdateServiceCheck.date).fromNow()}`}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </Stack>
                    </Stack>
                  </Box>
                </Grid>
              )
            })}
          </Grid>
        </Stack>
      </DetailsBox>
      <BigModal
        autoWidth
        open={openAddNew || openEdit}
        onClose={() => {
          setOpenAddNew(false)
          setOpenPDF(null)
          setOpenEdit(null)
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={1}
          component="form"
          onSubmit={handleSubmit}
        >
          <Typography variant="title2" align="center" fontSize="0.9rem">
            {openAddNew ? 'Add a new' : 'Edit'} DBS Check for{' '}
            {employeeData?.firstName} {employeeData?.lastName}
          </Typography>
          <FormControl fullWidth required>
            <LocalizationProvider
              dateAdapter={AdapterMoment}
              adapterLocale="en-GB"
            >
              <AnDateField
                disableFuture
                label="Date"
                value={date}
                onChange={(newDate) => setDate(newDate)}
                size="small"
                required
              />
            </LocalizationProvider>
          </FormControl>
          <TextField
            label="Certificate Number"
            value={number}
            required
            onChange={(e) => setNumber(e.target.value)}
            size="small"
            autoComplete="off"
          />
          <FormControlLabel
            control={
              <Switch
                checked={updateService}
                onChange={(e) => setUpdateService(e.target.checked)}
              />
            }
            label="Monitor this DBS certificate using the DBS Update Service"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            Only enable this where the organisation has authority to carry out
            Update Service checks.
          </Typography>
          <Button
            variant="outlined"
            color={attachment ? 'anGreen' : 'anBlue'}
            startIcon={<CloudUpload />}
            onClick={() => fileInput.current.click()}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              py: 1.2,
              borderStyle: 'dashed',
            }}
          >
            {attachment
              ? attachment.name
              : openEdit?.attachment
                ? 'Replace PDF attachment'
                : 'Upload DBS certificate PDF'}
          </Button>
          {fileError && (
            <Typography variant="caption" color="error">
              {fileError}
            </Typography>
          )}
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf"
            onChange={handleAttachmentAdd}
            style={{ display: 'none' }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={pendingUpload || !date || !number}
          >
            {pendingUpload ? 'Please wait...' : 'Submit'}
          </Button>
        </Box>
      </BigModal>
      <SmallModal open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <ConfirmDelete
          s3key={deleteTarget?.attachment}
          dbsID={deleteTarget?._id}
          onClose={() => setDeleteTarget(null)}
          employeeID={employee._id}
          loadEmployee={loadEmployee}
        />
      </SmallModal>
      <SmallModal
        open={openPDF && !openPDF?.attachment}
        onClose={() => setOpenPDF(null)}
      >
        <DBSInfoPanel>
          <Alert severity="info">No Attachments</Alert>
        </DBSInfoPanel>
      </SmallModal>
      <PDFViewer
        open={!!openPDF?.attachment}
        onClose={() => {
          setOpenPDF(null)
          setOpenEdit(null)
        }}
        title={`DBS Check for ${employeeData?.firstName} ${employeeData?.lastName}`}
        s3key={openPDF?.attachment}
      >
        <DBSInfoPanel />
      </PDFViewer>
    </Grid>
  )
}

export default DBSCheck
